'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { RegistrationWelcomeModal } from './RegistrationWelcomeModal';
import { RegistrationCompleteModal } from './RegistrationCompleteModal';
import { REGISTRATION_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = REGISTRATION_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * RegistrationTourOverlay - Renders the Registration tour step UI.
 *
 * Only renders when the active tour is 'registration'. Navigates the user
 * to the /10dlc page and walks them through the registration concepts.
 */
export function RegistrationTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isRegistrationTour = activeTourId === 'registration';

  // Auto-advance polling
  useTourAutoAdvance(isRegistrationTour, currentStep, nextStep);

  const handleWelcomeComplete = useCallback(() => {
    // Navigate to /10dlc with tour param — the compliance layout's
    // TourProvider will auto-start the tour at step 1 (past the welcome modal)
    router.push('/10dlc?tour=registration');
    reduxLogger.info('[RegistrationTour] Navigating to /10dlc with tour param');
  }, [router]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('registration');
    // Stay on /10dlc so the user can immediately start registering
  }, [completeTourWithTracking]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isRegistrationTour || !isActive || !currentStep) return null;

  // Backdrop visibility
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'registration_welcome' && (
            <RegistrationWelcomeModal onComplete={handleWelcomeComplete} />
          )}
          {currentStep.id === 'registration_complete' && (
            <RegistrationCompleteModal onFinish={handleFinish} />
          )}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
